
import { defineStore } from 'pinia';
import { useDataStore } from './data.js';
import { axios_instance as axios } from '../helpers/axios.js';
import { router } from '../router.js';

import gql_query_viewer from '../graphql/query/Viewer.gql';
import gql_query_project_locations from '../graphql/query/ProjectLocations.gql';
import gql_query_intended_uses from '../graphql/query/IntendedUses.gql';
import gql_query_offices from '../graphql/query/Offices.gql';
import gql_query_billing_locations from '../graphql/query/BillingLocations.gql';
import gql_query_project_types from '../graphql/query/ProjectTypes.gql';
import gql_query_companies from '../graphql/query/Companies.gql';
import gql_query_media_types from '../graphql/query/MediaTypes.gql';
import gql_query_currencies from '../graphql/query/Currencies.gql';

import gql_mutation_create_project from '../graphql/mutation/CreateProject.gql';
import gql_mutation_validate_new_project from '../graphql/mutation/ValidateNewProject.gql';
import gql_mutation_create_company from '../graphql/mutation/CreateCompany.gql';
import gql_mutation_create_client_user from '../graphql/mutation/CreateClientUser.gql';
import gql_mutation_create_project_location from '../graphql/mutation/CreateProjectLocation.gql';

const formatDropdownFilterOptions = ( data, options ) => { // eslint-disable-line snakecasejs/snakecasejs
	const label_key = options && options.label_key ? options.label_key : 'title';
	const value_key = options && options.value_key ? options.value_key : 'id';
	return data.map( element => {
		return {
			label: element[label_key],
			value: element[value_key],
			element
		};
	} );
};

const formatMultiLevelDropdownFilterOptions = ( data, options ) => { // eslint-disable-line snakecasejs/snakecasejs
	const title_key = options && options.title_key ? options.title_key : 'title';
	const id_key = options && options.id_key ? options.id_key : 'id';
	const level_key = options && options.level_key ? options.level_key : 'level';
	const children_key = options && options.children_key ? options.children_key : 'children';
	return data.map( element => {
		return {
			title: element[title_key],
			id: element[id_key],
			level: element[level_key],
			children: element[children_key]
		};
	} );
};

const normaliseString = ( string ) => { // eslint-disable-line snakecasejs/snakecasejs
	return string.normalize( 'NFD' ).replace( /[\u0300-\u036f]/g, '' );
};

const filterResultsByQuery = ( results, query ) => { // eslint-disable-line snakecasejs/snakecasejs
	const normalised_query = normaliseString( query.toLowerCase() );
	return results.filter( item => {
		const normalised_item_label = ( item.label !== null ) ? normaliseString( item.label.toLowerCase() ) : '';
		return normalised_item_label.includes( normalised_query );
	} );
};

export const useNewProjectStore = defineStore( 'new-project', { // eslint-disable-line snakecasejs/snakecasejs
	persist: true,
	state: () => {
		return {
			steps: [
				{ step_number: 1, title: 'Project details' },
				{ step_number: 2, title: 'Client information' },
				{ step_number: 3, title: 'Design team, developer & partners' },
				{ step_number: 4, title: 'Internal team' },
				{ step_number: 5, title: 'Project Scope' },
				{ step_number: 6, title: 'Review and create project' },
			],
			active_step_number: 1,
			user_profile: null,
			// Values from Craft
			boundary_staff_users: [],
			client_users: [],
			companies: [],
			project_types: [],
			media_types: [],
			project_locations: [],
			billing_locations: [],
			offices: [],
			currencies: [],
			intended_uses: [],
			// Form Fields
			form_fields: {
				pandadoc_id: null,
				project_name: null,
				project_type: null,
				intended_use: null,
				project_location: null,
				project_coordinates: null,
				kick_off: null,
				completion_date: null,
				project_description: null,
				client: {
					value: null,
					search_query: ''
				},
				key_client_contact: {
					values: [],
					search_query: ''
				},
				client_billing_contact: {
					values: [],
					search_query: ''
				},
				email_client_when_live: true,
				clients: {
					values: [],
					search_query: ''
				},
				architect: {
					values: [],
					search_query: ''
				},
				landscape_architect: {
					values: [],
					search_query: ''
				},
				interior_design: {
					values: [],
					search_query: ''
				},
				developer: {
					values: [],
					search_query: ''
				},
				creative_agency: {
					values: [],
					search_query: ''
				},
				brand_partner: {
					values: [],
					search_query: ''
				},
				sales_marketing: {
					values: [],
					search_query: ''
				},
				hospitality_operator: {
					values: [],
					search_query: ''
				},
				business_development: {
					values: [],
					search_query: ''
				},
				project_manager: {
					values: [],
					search_query: ''
				},
				staff_notifications: [],
				notify_slack_channel: true,
				billing_location: null,
				production_office: null,
				currency: null,
				project_scope: null,
				totals: []
			},
			form_errors: {},
			company_form_fields: {
				title: null,
				company_url: null,
				company_short_code: null
			},
			company_form_errors: null,
			client_user_form_fields: {
				full_name: null,
				email: null,
				role: null,
				company_id: null
			},
			client_user_form_errors: null,
			project_location_form_fields: {
				title: null,
				company_url: null
			},
			project_location_form_errors: null,
		};
	},
	actions: {
		async getPandaDocInfo() {
			this.form_errors['pandadoc_id'] = null;
			if ( !this.form_fields.pandadoc_id || '' === this.form_fields.pandadoc_id ) {
				this.form_errors['pandadoc_id'] = 'Please enter a valid ID';
				return false;
			}
			try {
				const response = await axios.get( '/panda-doc/project-info?id=' + this.form_fields.pandadoc_id );
				if ( response && response.data ) {
					this.form_fields.pandadoc_id = response.data.pandadoc_id ?? '';
					this.form_fields.project_name = response.data.project_name ?? '';
					this.form_fields.currency = response.data.currency ?? '';
					this.form_fields.project_scope = response.data.project_scope ?? null;
					return true;
				}
			} catch ( error ) {
				throw error.response.data;
			}
		},
		setFilterSearchQuery( value, field_handle ) {
			this.form_fields[field_handle].search_query = value;
		},
		setButtonDropdownValue( value, field_handle ) {
			this.form_fields[field_handle].value = value;
		},
		setCheckboxDropdownValue( value, field_handle ) {
			if ( this.form_fields[field_handle].values.find( existing_value => existing_value === value ) ) {
				return;
			}
			this.form_fields[field_handle].values.push( value );
		},
		removeCheckboxDropdownValue( value, field_handle ) {
			const index_to_remove = this.form_fields[field_handle].values.findIndex( active_value => active_value === value );
			if ( index_to_remove < 0 ) {
				return;
			}
			this.form_fields[field_handle].values.splice( index_to_remove, 1 );
		},
		setActiveStep( number ) {
			this.active_step_number = number;
		},
		async getBoundaryStaffUsers() {
			const data_store = useDataStore();
			try {
				const response = await data_store.getBoundaryStaffUsers();
				if ( !response || !response.data ) {
					return;
				}
				this.boundary_staff_users = formatDropdownFilterOptions( response.data.users, { label_key: 'fullName' } );
			} catch ( error ) {
				return error;
			}
		},
		async getUserProfile() {
			const store = useDataStore();
			const response = await store.authenticatedQuery( gql_query_viewer );
			if ( response.errors ) {
				return { error: response.errors[0].message };
			}
			if ( response.data && response.data.viewer ) {
				this.user_profile = response.data.viewer;
			}
		},
		async getClientUsers() {
			const data_store = useDataStore();
			try {
				const response = await data_store.getClientUsers();
				if ( !response || !response.data ) {
					return;
				}
				this.client_users = formatDropdownFilterOptions( response.data.users, { label_key: 'fullName' } );
			} catch ( error ) {
				return error;
			}
		},
		async getCompanies() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_companies, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.companies = formatDropdownFilterOptions( response.data.entries );
			} catch ( error ) {
				return error;
			}
		},
		async getProjectLocations() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_locations, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.project_locations = formatMultiLevelDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getIntendedUses() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_intended_uses, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.intended_uses = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getOffices() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_offices, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.offices = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getBillingLocations() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_billing_locations, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.billing_locations = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getProjectTypes() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_types, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.project_types = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getMediaTypes() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_media_types, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.media_types = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getCurrencies() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_currencies, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.currencies = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async validateNewProjectStep() {
			// send custom graphql mutation with all the form fields, plus active step
			// validate relevant fields based on step and return errors object
			this.form_errors = {};

			const form_data = { ...this.form_fields };

			form_data['step_number'] = this.active_step_number;
			form_data['client'] = this.form_fields.client.value;

			[
				'key_client_contact',
				'client_billing_contact',
				'clients',
				'architect',
				'landscape_architect',
				'interior_design',
				'developer',
				'creative_agency',
				'brand_partner',
				'sales_marketing',
				'hospitality_operator',
				'business_development',
				'project_manager'
			].forEach( property => form_data[property] = this.form_fields[property].values );

			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_mutation_validate_new_project, form_data );
				if ( !response || !response.data ) {
					return;
				}

				const errors = response.data.validateNewProject.errors;
				if ( !errors || !errors.length ) {
						this.active_step_number++;
					return;
				}

				errors.forEach( error => this.form_errors[error.field] = error.messages );
			} catch ( error ) {
				return error;
			}
		},
		async submitNewProject() {
			this.form_errors = {};

			const form_data = { ...this.form_fields };

			form_data['client'] = [parseInt( this.form_fields.client.value )];
			form_data['kick_off'] = this.form_fields.kick_off ? new Date( this.form_fields.kick_off ) : null;
			form_data['completion_date'] = this.form_fields.completion_date ? new Date( this.form_fields.completion_date ) : null;

			[
				'billing_location',
				'currency',
				'intended_use',
				'production_office',
				'project_location'
			].forEach( property => form_data[property] = [parseInt( this.form_fields[property] )] );

			[
				'key_client_contact',
				'client_billing_contact',
				'clients',
				'architect',
				'landscape_architect',
				'interior_design',
				'developer',
				'creative_agency',
				'brand_partner',
				'sales_marketing',
				'hospitality_operator',
				'business_development',
				'project_manager'
			].forEach( property => form_data[property] = this.form_fields[property].values.map( value => parseInt( value ) ) );

			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_mutation_create_project, form_data );
				if ( !response || !response.data || !response.data.createNewProject ) {
					return;
				}

				router.push( {
					name: 'projects__project',
					params: {
						project_id: response.data.createNewProject.id
					}
				} );

				this.$reset();
			} catch ( error ) {
				return error;
			}
		},
		async createCompany() {
			this.company_form_errors = '';
			const form_data = { ...this.company_form_fields };

			const company_with_existing_name = this.companies.find( ( { label } ) => label === form_data.title );
			if ( company_with_existing_name ) {
				return this.company_form_errors = 'This company already exists';
			}

			const company_with_existing_short_code = this.companies.find( ( { element } ) => element.company_short_code === form_data.company_short_code );
			if ( company_with_existing_short_code ) {
				return this.company_form_errors = 'Short code is already in use';
			}

			const short_code_regex = /^[A-Za-z]{1,5}$/;
			if ( !short_code_regex.test( form_data.company_short_code ) ) {
				return this.company_form_errors = 'Company short code must contain only letters (A-Z) and be 1-5 characters long.';
			}

			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_mutation_create_company, {
					title: form_data.title,
					company_url: form_data.company_url,
					company_short_code: form_data.company_short_code
				} );

				if ( !response ) {
					return;
				}

				const errors = response.errors;
				if ( !errors || !errors.length ) {
					return;
				}

				errors.forEach( error => this.company_form_errors = error.message );
			} catch ( error ) {
				return error;
			}
		},
		async createClientUser() {
			this.client_user_form_errors = '';
			const form_data = { ...this.client_user_form_fields };

			const existing_item = this.client_users.find( ( { label } ) => label === form_data.full_name );
			if ( existing_item ) {
				return this.client_user_form_errors = 'This client user already exists';
			}
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_mutation_create_client_user, {
					full_name: form_data.full_name,
					email: form_data.email,
					role: form_data.role,
					company_id: form_data.company_id
				} );

				if ( !response ) {
					return;
				}

				const errors = response.errors;
				if ( !errors || !errors.length ) {
					return;
				}

				errors.forEach( error => this.client_user_form_errors = error.message );
			} catch ( error ) {
				return error;
			}
		},
		async createProjectLocation() {
			this.project_location_form_errors = '';
			const form_data = { ...this.project_location_form_fields };

			const existing_item = this.project_locations.find( ( { label } ) => label === form_data.title );
			if ( existing_item ) {
				return this.project_location_form_errors = 'This location already exists';
			}

			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_mutation_create_project_location, {
					title: form_data.title,
					parent_id: form_data.parent_id
				} );

				if ( !response ) {
					return;
				}

				const errors = response.errors;
				if ( !errors || !errors.length ) {
					return;
				}

				errors.forEach( error => this.project_location_form_errors = error.message );
			} catch ( error ) {
				return error;
			}
		},
	},
	getters: {
		filtered_project_managers: ( state ) => {
			return filterResultsByQuery( state.boundary_staff_users, state.project_manager.search_query );
		},
		filtered_lead_artists: ( state ) => {
			return filterResultsByQuery( state.boundary_staff_users, state.lead_artist.search_query );
		},
		filtered_artists: ( state ) => {
			return filterResultsByQuery( state.boundary_staff_users, state.lead_artist.search_query );
		},
		filtered_business_developers: ( state ) => {
			return filterResultsByQuery( state.boundary_staff_users, state.form_fields.business_development.search_query );
		},
		filtered_project_managers: ( state ) => {
			return filterResultsByQuery( state.boundary_staff_users, state.form_fields.project_manager.search_query );
		},
		filtered_client_companies: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.client.search_query );
		},
		filtered_key_client_contacts: ( state ) => {
			return filterResultsByQuery( state.client_users, state.form_fields.key_client_contact.search_query );
		},
		filtered_client_billing_contacts: ( state ) => {
			return filterResultsByQuery( state.client_users, state.form_fields.client_billing_contact.search_query );
		},
		filtered_clients: ( state ) => {
			return filterResultsByQuery( state.client_users, state.form_fields.clients.search_query );
		},
		filtered_brand_partners: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.brand_partner.search_query );
		},
		filtered_developers: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.developer.search_query );
		},
		filtered_development_consultants: ( state ) => {
			return filterResultsByQuery( state.companies, state.development_consultant.search_query );
		},
		filtered_creative_agencies: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.creative_agency.search_query );
		},
		filtered_sales_marketing: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.sales_marketing.search_query );
		},
		filtered_hospitality_operators: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.hospitality_operator.search_query );
		},
		filtered_architects: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.architect.search_query );
		},
		filtered_landscape_architects: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.landscape_architect.search_query );
		},
		filtered_interior_designers: ( state ) => {
			return filterResultsByQuery( state.companies, state.form_fields.interior_design.search_query );
		},
		label_project_type: ( state ) => {
			const item = state.project_types.find( ( { value } ) => value === state.form_fields.project_type );
			if ( !item || !item.label ) {
				return;
			}
			return item.label;
		},
		selected_project_location: ( state ) => {
			// const item = state.project_locations.find( ( { value } ) => value === state.form_fields.project_location );
			// if ( !item || !item.label ) {
			// 	return;
			// }
			// return item;
			let item = null;
			state.project_locations.forEach( location => {
				if ( location.id === String( state.form_fields.project_location ) ) {
					item = location;
					if ( !item || !item.title ) {
						return;
					}
				}
			} );
			return item;
		},
		selected_intended_use: ( state ) => {
			const item = state.intended_uses.find( ( { value } ) => value === state.form_fields.intended_use );
			if ( !item || !item.label ) {
				return;
			}
			return item;
		},
		selected_client: ( state ) => {
			const item = state.companies.find( ( { value } ) => value === state.form_fields.client.value );
			if ( !item || !item.label ) {
				return;
			}
			return item;
		},
		selected_key_client_contacts: ( state ) => {
			const items = [];
			state.form_fields.key_client_contact.values.forEach( key_client_contact => {
				const item = state.client_users.find( ( { value } ) => value === key_client_contact );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_client_billing_contacts: ( state ) => {
			const items = [];
			state.form_fields.client_billing_contact.values.forEach( client_billing_contact => {
				const item = state.client_users.find( ( { value } ) => value === client_billing_contact );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_clients: ( state ) => {
			const items = [];
			state.form_fields.clients.values.forEach( client => {
				const item = state.client_users.find( ( { value } ) => value === client );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_architects: ( state ) => {
			const items = [];
			state.form_fields.architect.values.forEach( architect => {
				const item = state.companies.find( ( { value } ) => value === architect );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_landscape_architects: ( state ) => {
			const items = [];
			state.form_fields.landscape_architect.values.forEach( landscape_architect => {
				const item = state.companies.find( ( { value } ) => value === landscape_architect );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_interior_designers: ( state ) => {
			const items = [];
			state.form_fields.interior_design.values.forEach( interior_designer => {
				const item = state.companies.find( ( { value } ) => value === interior_designer );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_developers: ( state ) => {
			const items = [];
			state.form_fields.developer.values.forEach( developer => {
				const item = state.companies.find( ( { value } ) => value === developer );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_creative_agencies: ( state ) => {
			const items = [];
			state.form_fields.creative_agency.values.forEach( creative_agency => {
				const item = state.companies.find( ( { value } ) => value === creative_agency );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_brand_partners: ( state ) => {
			const items = [];
			state.form_fields.brand_partner.values.forEach( brand_partner => {
				const item = state.companies.find( ( { value } ) => value === brand_partner );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_sales_marketing_partners: ( state ) => {
			const items = [];
			state.form_fields.sales_marketing.values.forEach( sales_marketing => {
				const item = state.companies.find( ( { value } ) => value === sales_marketing );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_hospitality_operators: ( state ) => {
			const items = [];
			state.form_fields.hospitality_operator.values.forEach( hospitality_operator => {
				const item = state.companies.find( ( { value } ) => value === hospitality_operator );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_business_development_leads: ( state ) => {
			const items = [];
			state.form_fields.business_development.values.forEach( business_development => {
				const item = state.boundary_staff_users.find( ( { value } ) => value === business_development );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		},
		selected_project_managers: ( state ) => {
			const items = [];
			state.form_fields.project_manager.values.forEach( project_manager => {
				const item = state.boundary_staff_users.find( ( { value } ) => value === project_manager );
				if ( !item || !item.label ) {
					return;
				}
				items.push( item );
			} );
			return items;
		}
	}
} );
